import { Helmet } from "react-helmet-async";

// Single source of truth for the production origin used to build absolute
// canonical / OG URLs. We intentionally do NOT use window.location here:
// during SSG prerender `window` is undefined, and even on the client the
// canonical must always point at the real production domain.
export const SITE_URL = "https://www.blueheronmarketing.com";

// Default social share image (matches the original static index.html OG image).
export const DEFAULT_OG_IMAGE =
  "https://storage.googleapis.com/gpt-engineer-file-uploads/kkARH5MXLQcZjEW9jP3SDlUcVQl1/social-images/social-1769633757688-blue heron.jpg";

export interface SeoProps {
  /** Full <title> text. */
  title: string;
  /** Meta description. */
  description: string;
  /** Route path beginning with "/", e.g. "/about". Used to build the canonical URL. */
  path: string;
  /** When true, emits noindex,nofollow for robots + googlebot. */
  noindex?: boolean;
  /** Absolute OG/Twitter image URL. Defaults to the shared brand image. */
  image?: string;
  /** og:type, defaults to "website". */
  type?: string;
  /** One or more JSON-LD objects rendered as <script type="application/ld+json">. */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

/**
 * Reusable head/SEO block. This is the single head-management mechanism for the
 * site (react-helmet-async). The SSG build extracts whatever this renders into
 * the static <head> of each prerendered route.
 */
const Seo = ({
  title,
  description,
  path,
  noindex = false,
  image = DEFAULT_OG_IMAGE,
  type = "website",
  jsonLd,
}: SeoProps) => {
  const canonical = `${SITE_URL}${path === "/" ? "/" : path}`;
  const schemas = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {noindex && <meta name="googlebot" content="noindex, nofollow" />}

      <meta property="og:type" content={type} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {schemas.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default Seo;
